import { useEffect, useRef } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { getRandomInt } from '~/utils/random';
import { SECOND } from '~/constants';
import { useAssetStoreState } from '~/store';
import { SelectedAvatar } from './Avatar';

interface ModelProps {
  character: SelectedAvatar;
}

export default function Model({ character }: ModelProps) {
  const gltf = useLoader(GLTFLoader, character.characterUrl);
  const mixer = useRef<THREE.AnimationMixer>();

  const animationState = useAssetStoreState((state) => state.animationState);
  const currentAnimation = useAssetStoreState(
    (state) => state.currentAnimation,
  );
  const animationList = useAssetStoreState((state) => state.animations);

  const {
    setAnimations,
    setCurrentAnimation,
    setAnimationState,
    resetAvatarState,
  } = useAssetStoreState((state) => ({
    setAnimations: state.setAnimations,
    setCurrentAnimation: state.setCurrentAnimation,
    setAnimationState: state.setAnimationState,
    resetAvatarState: state.resetAvatarState,
  }));

  useEffect(() => {
    return () => resetAvatarState();
  }, []);

  useEffect(() => {
    mixer.current = new THREE.AnimationMixer(gltf.scene);
    setAnimations(gltf.animations);
  }, [gltf]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const setAnimation = async () => {
      const type = animationState?.type;
      if (type === 'Idle') {
        const idleArray = animationList?.filter((el) => el.name.includes(type));
        if (idleArray) {
          const idleArrayLength = idleArray.length;
          if (idleArrayLength > 0) {
            const randomInt = getRandomInt(0, idleArrayLength - 1);

            const target = idleArray[randomInt];
            setCurrentAnimation(target);
            if (type === 'Idle') {
              timeout = setTimeout(() => {
                setAnimationState({
                  type: 'Idle',
                });
              }, target.duration * SECOND);
            }
          }
        }
      }
      if (type === 'external') {
        try {
          const loader = new GLTFLoader();
          const anim = await loader.loadAsync(animationState.animationName);
          const target = anim?.animations[0];
          if (target) {
            setCurrentAnimation(target);
          }
        } finally {
          //
        }
      }
    };
    setAnimation();

    return () => clearTimeout(timeout);
  }, [animationList, animationState]);

  useEffect(() => {
    if (!mixer.current) return;

    if (currentAnimation) {
      const action = mixer.current.clipAction(currentAnimation);

      action.reset().fadeIn(1).play();

      return () => {
        action.fadeOut(1);
      };
    }
  }, [currentAnimation?.uuid]);

  useFrame((_, delta) => {
    mixer?.current?.update(delta);
  });

  return (
    <primitive key={character.id} object={gltf.scene} position={[0, -1, 0]} />
  );
}
